import {Box, SelectChangeEvent} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  assignCustodian,
  getProcessingSettings,
  initiateProcessing,
} from '../../../../api/EvidenceSourceUpload';
import {getAPIErrorDetail} from '../../../../api/utils';
import {ReactComponent as AddIcon} from '../../../../assests/svg/plus-icon-success.svg';
import {useEvidenceCustodian} from '../../../../hooks/upload-evidence-source/useEvidenceCustodian';
import {RootState} from '../../../../redux/store';
import {
  AssignCustodianPayload,
  CustodianDto,
  CustodianPayload,
  InitiateProcessingPayload,
  ProcessingSettingsData,
  ToastState,
} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurDrawerPanelContainer from '../../../client-details/AurDrawerPanelContainer';
import AurAcknowlegment from '../../../common/aur-acknowledgement/AurAcknowledgement';
import AurAddCustodianModel from '../../../common/aur-add-custodian-model/AurAddCustodianModel';
import AurButton from '../../../common/aur-button/AurButton';
import AurDrawer from '../../../common/aur-drawer/AurDrawer';
import AurDrawerHeader from '../../../common/aur-drawer/AurDrawerHeader';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {AurSelect, Option} from '../../../common/aur-select/AurSelect';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurToast from '../../../common/aur-toast/AurToast';
import {SelectedProjectEvidenceType} from '../EvidenceSourceGrid';
import DefaultProcessingSettings from './DefaultProcessingSettings';
import _ from 'lodash';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

const CLIENT_REFERENCE_MAX_LENGTH = 15;

interface InitiateProcessDrawerProps {
  open: boolean;
  onClose: () => void;
  projectEvidenceDetails: SelectedProjectEvidenceType | null;
  onProcessingInitiated: () => void;
  onCustodianAssigned: () => void;
}

const ACTION_VALUE = 'initiate-processing-add-custodian';

const InitiateProcessDrawer = ({
  open,
  onClose,
  projectEvidenceDetails,
  onProcessingInitiated,
  onCustodianAssigned,
}: InitiateProcessDrawerProps) => {
  const {trackGAEvent} = useAnalytics();

  const [addCustodianPopup, setAddCustodianPopup] = useState<boolean>(false);
  const [custodian, setCustodian] = useState<CustodianDto>();
  const [clientReference, setClientReference] = useState<string>(
    projectEvidenceDetails?.clientReferenceNumber ?? ''
  );
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);
  const acknowledgementDateTimeRef = useRef<string>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [defaultProcessingSettings, setDefaultProcessingSettings] =
    useState<ProcessingSettingsData>({});
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const projectId = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectId ?? ''
  );

  useEffect(() => {
    if (addCustodianPopup) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t(
            'gaTracking.manageProjectEvidence.initiateProcessingAddCustodian'
          ),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  }, [addCustodianPopup]);

  const {
    custodians,
    addNewCustodian,
    isLoading: isCustodianLoading,
  } = useEvidenceCustodian(clientId, projectId);

  const custodianApiOptions: Option[] = custodians.map((custodian) => ({
    key: String(custodian.id),
    value: String(custodian.id),
    label: custodian.displayName,
  }));

  const fetchProcessingSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getProcessingSettings(clientId, projectId);

      if (response?.data?.meta?.status === 'Success') {
        setDefaultProcessingSettings(response?.data?.data ?? {});
      }
    } catch (error: any) {
      setToast({
        open: true,
        severity: 'error',
        message: error?.response?.data?.meta?.message || '',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && projectId) {
      fetchProcessingSettings();
    }
  }, [clientId, projectId]);

  useEffect(() => {
    if (
      projectEvidenceDetails?.custodianId?.toString() &&
      custodians.length > 0
    ) {
      const selectedCustodian = custodians.find(
        (custodian) =>
          String(custodian.id) ===
          projectEvidenceDetails?.custodianId?.toString()
      );

      if (selectedCustodian) {
        setCustodian(selectedCustodian);
      }
    }
  }, [projectEvidenceDetails, custodians]);

  const panelHeader = (
    <Box className="panel-header">
      <AurButton
        label={t('common.close')}
        type="exit"
        classNames={'drawer-close-button'}
        size="Medium"
        title={t('common.close')}
        name={t('common.close')}
        onClick={onClose}
        dataTestId="initiate-process-drawer-close"
      />
    </Box>
  );

  const addCustodianOption: Option = {
    key: 'add',
    value: ACTION_VALUE,
    label: '',
    displayElement: (
      <Box className="add-custodian-option-1">
        <AddIcon />
        <span className="add-custodian-text">
          {t('evidenceSource.assignedCustodian.addCustodian')}
        </span>
      </Box>
    ),
  };

  const custodianOptions: Option[] = [
    addCustodianOption,
    ...custodianApiOptions,
  ];

  const assignCustodianFunction = useCallback(
    async (payload: AssignCustodianPayload) => {
      setIsLoading(true);
      try {
        const response = await assignCustodian(clientId, projectId, payload);

        if (response?.data?.meta?.status === 'Success') {
          setToast({
            open: true,
            severity: 'success',
            message: response?.data?.meta?.message,
          });
        }
      } catch (error: any) {
        const apiError = getAPIErrorDetail(error);
        let message = '';

        if (apiError.validationErrors?.length == 0) {
          message = apiError.meta?.message || '';
        } else {
          message = apiError?.validationErrors?.[0]?.message?.[0] || '';
        }

        setToast({
          open: true,
          severity: 'error',
          message,
        });
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const onClickTrackingEvent = (
    name: string,
    action: string,
    type: string,
    region: string,
    subPage?: {
      subpage_name: string;
      subpage_type: string;
    }
  ) => {
    const eventObject: any = {
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type,
        region,
        action,
      },
    };

    if (subPage) {
      eventObject.subPage = subPage;
    }

    trackGAEvent(eventObject);
  };

  const handleCustodianChange = async (
    event: SelectChangeEvent<string>
  ): Promise<void> => {
    const value = event.target.value;
    if (value === ACTION_VALUE) {
      onClickTrackingEvent(
        t('gaTracking.manageProjectEvidence.addCustodian'),
        t('gaTracking.manageProjectEvidence.addCustodianBegin'),
        GA_CLICK_TYPES.DROPDOWN,
        GA_REGIONS.BODY,
        {
          subpage_name: t(
            'gaTracking.manageProjectEvidence.initiateProcessing'
          ),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        }
      );
      setAddCustodianPopup(true);
      return;
    }
    if (value !== custodian?.id?.toString()) {
      onClickTrackingEvent(
        t('gaTracking.manageProjectEvidence.newCustodian'),
        t('gaTracking.manageProjectEvidence.custodianChanged'),
        GA_CLICK_TYPES.BUTTON,
        GA_REGIONS.BODY,
        {
          subpage_name: t(
            'gaTracking.manageProjectEvidence.initiateProcessingAddCustodian'
          ),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        }
      );
    }

    const selectedCustodian = custodians.find(
      (custodian) => String(custodian.id) === value
    );
    if (selectedCustodian) {
      setCustodian(selectedCustodian);

      const payload = {
        custodianId: selectedCustodian.id,
        batchingGroup: projectEvidenceDetails?.batchingGroup || null,
      };
      assignCustodianFunction(payload);
      onCustodianAssigned();
    }
  };

  const handleAcknowledgeChange = useCallback(
    (isAcknowledged: boolean, acknowledgementDateTime?: string) => {
      acknowledgementDateTimeRef.current = acknowledgementDateTime;
      setIsAcknowledged(isAcknowledged);
    },
    []
  );

  const handleInitiateClick = () => {
    const payload = {
      evidMediaId: projectEvidenceDetails?.evidMediaId ?? 0,
      batchingGroupId: projectEvidenceDetails?.batchingGroup ?? 0,
      quadS: projectEvidenceDetails?.quadS ?? '',
      safeId: projectEvidenceDetails?.safeId ?? 0,
      receiptMethod: projectEvidenceDetails?.receiptMethod ?? '',
    };
    initiateProcessingFunction(payload);
  };

  const initiateProcessingFunction = useCallback(
    async (payload: InitiateProcessingPayload) => {
      onClickTrackingEvent(
        t('gaTracking.manageProjectEvidence.initiateProcessing'),
        t('gaTracking.manageProjectEvidence.actionMenuProcessingComplete'),
        GA_CLICK_TYPES.BUTTON,
        GA_REGIONS.BODY,
        {
          subpage_name: t(
            'gaTracking.manageProjectEvidence.initiateProcessingAddCustodian'
          ),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        }
      );
      setIsLoading(true);
      try {
        const response = await initiateProcessing(clientId, projectId, payload);

        if (response?.data?.meta?.status === 'Success') {
          onProcessingInitiated();
        }
      } catch (error: any) {
        const message = getAPIErrorDetail(error).validationErrors?.[0];
        setToast({
          open: true,
          severity: 'error',
          message:
            message?.message?.[0] || error?.response?.data?.meta?.message,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [clientId, projectId]
  );

  const panelContainer = (
    <AurDrawerPanelContainer
      panelHeader={panelHeader}
      className='drawer-section-container'
      contentHeader={<></>}
      contentBody={
        <>
          <Box className="scrollable-content">
            <AurDrawerHeader
              title={t('evidenceSource.initiateProcess.title')}
              titleCssClass="typo-text headline1_size drawer-header-label"
              buttons={[]}
            />
            <Box className="drawer-content">
              <DefaultProcessingSettings
                settingsDetails={defaultProcessingSettings}
              />

              <Box className="custodian-select-wrapper">
                <AurSelect
                  label={t('evidenceSource.initiateProcess.assignedCustodian')}
                  name={t('evidenceSource.initiateProcess.assignedCustodian')}
                  optionList={custodianOptions}
                  isSingleSelection
                  field={{
                    value: custodian?.id?.toString() ?? '',
                    onChange: handleCustodianChange,
                  }}
                  menuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 376,
                        '& .MuiMenu-list': {
                          maxHeight: 376,
                          overflowY: 'auto',
                        },
                      },
                    },
                  }}
                  fieldState={{
                    error: undefined,
                  }}
                  onOpenDropdown={() => {
                    onClickTrackingEvent(
                      t('gaTracking.manageProjectEvidence.custodian'),
                      t(
                        'gaTracking.manageProjectEvidence.openedCustodianDropdown'
                      ),
                      GA_CLICK_TYPES.DROPDOWN,
                      GA_REGIONS.BODY,
                      {
                        subpage_name: t(
                          'gaTracking.manageProjectEvidence.initiateProcessing'
                        ),
                        subpage_type: GA_SUBPAGE_TYPE.MODAL,
                      }
                    );
                  }}
                />
              </Box>

              <AurTextField
                label={t(
                  'evidenceSource.initiateProcess.clientReferenceNumber'
                )}
                name={t('evidenceSource.initiateProcess.clientReferenceNumber')}
                className="text-field"
                variant="filled"
                value={clientReference}
                onChange={(e) => setClientReference(e.target.value)}
                ariaLabel={t(
                  'evidenceSource.initiateProcess.clientReferenceNumber'
                )}
                inputProps={{
                  maxLength: CLIENT_REFERENCE_MAX_LENGTH,
                  role: 'input',
                }}
                data-testid="client-reference-input"
                disabled
                aria-disabled
              />
            </Box>
          </Box>
          {/* acknowledgement section */}
          <AurAcknowlegment
            labelSubText={t(
              'evidenceSource.initiateProcess.acknowledgementMsg'
            )}
            showCheckbox={false}
            labelHeading={t('evidenceSource.initiateProcess.acknowledgement')}
            initialAcknowledgementDateTime={''}
            onAcknowledgeChange={handleAcknowledgeChange}
            showIntiateButton={true}
            initateBtnEnabled={isAcknowledged && !_.isNil(custodian)}
            onInitiateClick={handleInitiateClick}
          />

          <AurToast
            open={toast.open}
            message={toast.message}
            severity={toast.severity}
            hideDuration={5000}
            horizontalPosition="right"
            verticalPosition="bottom"
            onClose={() => setToast((prev) => ({...prev, open: false}))}
          />
        </>
      }
    />
  );

  const handleSubmitCustodian = async (data: CustodianPayload) => {
    onClickTrackingEvent(
      t('gaTracking.global.add'),
      t('gaTracking.manageProjectEvidence.addCustodianComplete'),
      GA_CLICK_TYPES.BUTTON,
      GA_REGIONS.MODAL,
      {
        subpage_name: t(
          'gaTracking.manageProjectEvidence.initiateProcessingAddCustodian'
        ),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      }
    );
    const result = await addNewCustodian({
      firstName: data.firstName,
      lastName: data.lastName,
    });

    if (result.success) {
      setToast({
        open: true,
        severity: 'success',
        message: result.message,
      });

      setAddCustodianPopup(false);
    } else {
      setToast({
        open: true,
        severity: 'error',
        message: result.message,
      });
    }
  };

  return (
    <>
      <AurDrawer
        className="initiate-process-drawer"
        data-testid="initiate-process-drawer"
        open={open}
        onClose={onClose}
        anchor="right"
        drawerContent={panelContainer}
      />
      {addCustodianPopup && (
        <AurAddCustodianModel
          isOpen={addCustodianPopup}
          onClose={() => {
            onClickTrackingEvent(
              t('gaTracking.global.close'),
              t('gaTracking.manageProjectEvidence.addCustodianClosedModal'),
              GA_CLICK_TYPES.BUTTON,
              GA_REGIONS.MODAL,
              {
                subpage_name: t(
                  'gaTracking.manageProjectEvidence.initiateProcessingAddCustodian'
                ),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              }
            );
            setAddCustodianPopup(false);
          }}
          keepMounted={false}
          onAddClick={handleSubmitCustodian}
          isLoading={isCustodianLoading}
          toast={toast}
          setToast={setToast}
        />
      )}

      <AurLoadingIndicator overlay={true} isOpen={isLoading} />
    </>
  );
};

export default InitiateProcessDrawer;
